import React, { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate
} from "react-router-dom";
import { Feed } from "../../components/feed";
import { AuthProvider, useAuth } from "../../contexts/AuthContext";
import AddPlace from "../../screens/AddPlace";
import BayAreaMap from "../../screens/BayAreaMap/BayAreaMap";
import FindPeople from "../../screens/FindPeople";
import LocalMap from "../../screens/LocalMap";
import MainPage from "../../screens/MainPage/MainPage";
import NewPost from "../../screens/NewPost";
import EditPost from "../../screens/EditPost";
import ProfileSetup from "../../screens/ProfileSetup";
import Regions from "../../screens/Regions/Regions";
import UserProfile from "../../screens/UserProfile";
import PostDetail from "../../screens/PostDetail/PostDetail";

import Header from "../Header";
import Leaderboards from "../../screens/Leaderboards/Leaderboards";
import FollowersList from "../../screens/UserProfile/FollowersList";
import FollowingList from "../../screens/UserProfile/FollowingList";
import "./App.css";
import supabase from '../../services/supabase';

// Create a layout component to handle routing
function Layout({ children, searchQuery, setSearchQuery, handleSearch }) {
  // Use the useLocation hook for reliable path checking
  const location = useLocation(); 
  const isLandingPage = location.pathname === "/";
  const isProfilePage = location.pathname.includes("/profile");
  
  return (
    <>
      <Header
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
      />
      <div className="layout-content"> 
        {children}
      </div>
    </>
  );
}

function App() {
  // Note: The Layout component needs to be *inside* the Router 
  // context for useLocation to work. Let's restructure slightly.
  return (
    <Router>
      <AuthProvider>
        {/* Render RoutesWrapper which contains Layout and Routes */}
        <RoutesWrapper /> 
      </AuthProvider>
    </Router>
  );
}

// New wrapper component to contain Layout and Routes within Router context
function RoutesWrapper() {
  const [searchQuery, setSearchQuery] = useState("");
  const mapSearchRef = React.useRef(null);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileChecked, setProfileChecked] = useState(false);

  useEffect(() => {
    async function checkProfileAndRedirect() {
    if (!loading) {
      // MainPage is now the landing page, no automatic redirects needed
      // Users can access MainPage whether logged in or not
      if (user && location.pathname === '/') {
        // If user is logged in and on main page, check if they need profile setup
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        // If no profile or missing required fields, redirect to profile setup
        if (!profile || !profile.username || !profile.name) {
          navigate('/profile-setup', { replace: true });
        }
        setProfileChecked(true);
      } else if (!user && location.pathname !== '/' && location.pathname !== '/profile-setup' && location.pathname !== '/regions' && location.pathname !== '/bayarea' && location.pathname !== '/addplace') {
        // Redirect to main page if not logged in and trying to access protected routes
        navigate("/", { replace: true });
      }
      }
    }
    checkProfileAndRedirect();
  }, [user, loading, navigate, location.pathname]);

  const handleSearch = (query, isUserSearch = false) => {
    if (isUserSearch) {
      // handle user search — maybe set a state or trigger effect in FindPeople
    } else if (mapSearchRef.current) {
      mapSearchRef.current(query);
    }
  };

  if (loading) {
    return <div>Loading authentication state...</div>;
  }

  return (
    <Layout 
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      handleSearch={handleSearch}
    >
      <Routes>
        <Route path="/" element={<MainPage />} />
        <Route path="/feed" element={<Feed />} />
        <Route path="/regions" element={<Regions />} />
        <Route path="/localmap" element={<LocalMap setSearchHandler={mapSearchRef} />} />
        <Route path="/mainpage" element={<MainPage />} />
        <Route path="/bayarea" element={<BayAreaMap />} />
        <Route path="/profile" element={<UserProfile />} />
        <Route path="/profile/:username" element={<UserProfile />} />
        <Route path="/profile/:username/followers" element={<FollowersList />} />
        <Route path="/profile/:username/following" element={<FollowingList />} />
        <Route path="/profile-setup" element={<ProfileSetup />} />
        <Route path="/addplace" element={<AddPlace setSearchHandler={mapSearchRef} />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/new-post" element={<NewPost />} />
        <Route path="/post/:postId/edit" element={<EditPost />} />
        <Route path="/find-people" element={<FindPeople searchQuery={searchQuery} />} />
        <Route path="/post/:postId" element={<PostDetail />} />
        <Route path="/leaderboards" element={<Leaderboards />} />
      </Routes>
    </Layout>
  );
}

export default App;
